/**
 * Everything the studio's Template control lists: the templates, the deck screens, saved reports.
 * Saved reports come from `GET /report-engine/reports`, so this list and Manage Reports cannot drift.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getStudioCatalog, listReports } from 'src/components/star-grid/api/reportEngineClient';
import type { CatalogGroup, ReportSummary, CatalogTemplate } from 'src/components/star-grid/api/reportEngineTypes';

export interface StudioTemplateSources {
  readonly templates: readonly CatalogTemplate[];
  readonly prophecy: readonly CatalogGroup[];
  readonly saved: readonly ReportSummary[];
  readonly loading: boolean;
  readonly error: string | undefined;
  readonly reload: () => void;
}

export function useStudioTemplateSources(enabled = true): StudioTemplateSources {
  const [templates, setTemplates] = useState<readonly CatalogTemplate[]>([]);
  const [prophecy, setProphecy] = useState<readonly CatalogGroup[]>([]);
  const [saved, setSaved] = useState<readonly ReportSummary[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    setLoading(true);
    // The saved-reports call is allowed to fail on its own: a template is still pickable when the
    // Postgres list is unreachable, and failing the whole control would block creating a report.
    Promise.all([getStudioCatalog(controller.signal), listReports(controller.signal).catch(() => [] as ReportSummary[])])
      .then(([catalog, reports]) => {
        setTemplates(catalog.templates);
        setProphecy(catalog.prophecy);
        setSaved(reports);
        setError(undefined);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled, attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  return useMemo(() => ({ templates, prophecy, saved, loading, error, reload }), [templates, prophecy, saved, loading, error, reload]);
}
